"use client";

import { useCallback, useEffect, useRef, useTransition } from "react";
import { useRouter } from "next/navigation";

type Router = ReturnType<typeof useRouter>;
type PushOptions = Parameters<Router["push"]>[1];
type ReplaceOptions = Parameters<Router["replace"]>[1];
type PrefetchOptions = Parameters<Router["prefetch"]>[1];

export interface AsyncRouter {
  isPending: boolean;
  back: () => Promise<void>;
  forward: () => Promise<void>;
  refresh: () => Promise<void>;
  push: (href: string, options?: PushOptions) => Promise<void>;
  replace: (href: string, options?: ReplaceOptions) => Promise<void>;
  prefetch: (href: string, options?: PrefetchOptions) => Promise<void>;
}

export const useAsyncRouter = (): AsyncRouter => {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const pendingResolversRef = useRef<Set<() => void>>(new Set());
  const isPendingRef = useRef(false);

  const resolveAllPending = useCallback(() => {
    pendingResolversRef.current.forEach((resolve) => resolve());
    pendingResolversRef.current.clear();
  }, []);

  useEffect(() => {
    isPendingRef.current = isPending;

    if (!isPending) {
      resolveAllPending();
    }
  }, [isPending, resolveAllPending]);

  useEffect(() => {
    return () => {
      resolveAllPending();
    };
  }, [resolveAllPending]);

  const runTransition = useCallback(
    (action: () => void) => {
      return new Promise<void>((resolve, reject) => {
        let settled = false;
        const resolvePromise = () => {
          if (!settled) {
            settled = true;
            resolve();
          }
        };

        pendingResolversRef.current.add(resolvePromise);

        try {
          startTransition(() => {
            action();
          });
        } catch (error) {
          pendingResolversRef.current.delete(resolvePromise);
          settled = true;
          reject(error);
          return;
        }

        setTimeout(() => {
          if (
            !isPendingRef.current &&
            pendingResolversRef.current.has(resolvePromise)
          ) {
            pendingResolversRef.current.delete(resolvePromise);
            resolvePromise();
          }
        }, 0);
      });
    },
    [startTransition],
  );

  const back = useCallback(() => {
    return runTransition(() => {
      router.back();
    });
  }, [router, runTransition]);

  const forward = useCallback(() => {
    return runTransition(() => {
      router.forward();
    });
  }, [router, runTransition]);

  const refresh = useCallback(() => {
    return runTransition(() => {
      router.refresh();
    });
  }, [router, runTransition]);

  const push = useCallback(
    (href: string, options?: PushOptions) => {
      return runTransition(() => {
        router.push(href, options);
      });
    },
    [router, runTransition],
  );

  const replace = useCallback(
    (href: string, options?: ReplaceOptions) => {
      return runTransition(() => {
        router.replace(href, options);
      });
    },
    [router, runTransition],
  );

  const prefetch = useCallback(
    (href: string, options?: PrefetchOptions) => {
      return new Promise<void>((resolve, reject) => {
        try {
          router.prefetch(href, options);
          resolve();
        } catch (error) {
          reject(error);
        }
      });
    },
    [router],
  );

  return {
    isPending,
    back,
    forward,
    prefetch,
    push,
    refresh,
    replace,
  };
};
